import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const Header = () => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const navigationItems = [
    { label: 'Home', path: '/home-landing', icon: 'Home' },
    { label: 'Launch', path: '/launch-agent', icon: 'Rocket' },
    { label: 'Dashboard', path: '/dashboard', icon: 'BarChart3' },
    { label: 'Integrations', path: '/integrations-management', icon: 'Plug' },
    { label: 'Settings', path: '/settings-administration', icon: 'Settings' }
  ];

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)')?.matches;
    const shouldUseDark = savedTheme === 'dark' || (!savedTheme && prefersDark);
    
    setIsDarkMode(shouldUseDark);
    document.documentElement?.classList?.toggle('dark', shouldUseDark);
  }, []);

  const toggleTheme = () => {
    const newTheme = !isDarkMode;
    setIsDarkMode(newTheme);
    document.documentElement?.classList?.toggle('dark', newTheme);
    localStorage.setItem('theme', newTheme ? 'dark' : 'light');
  };

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const isActivePath = (path) => {
    return location?.pathname === path;
  };

  const Logo = () => (
    <div 
      className="flex items-center space-x-3 cursor-pointer"
      onClick={() => handleNavigation('/home-landing')}
    >
      <div className="relative">
        <div className="w-8 h-8 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center">
          <Icon name="Brain" size={20} color="white" strokeWidth={2.5} />
        </div>
        <div className="absolute -top-1 -right-1 w-3 h-3 bg-accent rounded-full animate-pulse-soft"></div>
      </div>
      <div className="flex flex-col">
        <span className="text-lg font-semibold text-foreground leading-none">
          Ipsos Insight
        </span>
        <span className="text-xs text-text-secondary font-medium">
          Intelligence Platform
        </span>
      </div>
    </div>
  );

  const EnvironmentIndicator = () => (
    <div className="hidden lg:flex items-center space-x-2 px-3 py-1.5 bg-muted rounded-lg">
      <div className="w-2 h-2 bg-success rounded-full animate-pulse-soft"></div>
      <span className="text-sm font-medium text-muted-foreground">Production</span>
    </div>
  );

  const UserMenu = () => (
    <div className="relative">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
        className="flex items-center space-x-2 px-3"
      >
        <div className="w-8 h-8 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center">
          <span className="text-sm font-semibold text-white">JD</span>
        </div>
        <div className="hidden md:flex flex-col items-start">
          <span className="text-sm font-medium text-foreground">John Doe</span>
          <span className="text-xs text-text-secondary">Research Executive</span>
        </div>
        <Icon 
          name={isUserMenuOpen ? "ChevronUp" : "ChevronDown"} 
          size={16} 
          className="text-muted-foreground transition-transform duration-200"
        />
      </Button>

      {isUserMenuOpen && (
        <div className="absolute right-0 top-full mt-2 w-64 bg-popover border border-border rounded-lg shadow-lg z-200 animate-fade-in">
          <div className="p-4 border-b border-border">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center">
                <span className="text-sm font-semibold text-white">JD</span>
              </div>
              <div>
                <p className="font-medium text-popover-foreground">John Doe</p>
                <p className="text-sm text-text-secondary">john.doe@ipsos.com</p>
                <p className="text-xs text-text-secondary">Research Executive</p>
              </div>
            </div>
          </div>
          
          <div className="p-2">
            <button
              onClick={toggleTheme}
              className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-popover-foreground hover:bg-muted rounded-md transition-colors duration-200"
            >
              <Icon name={isDarkMode ? "Sun" : "Moon"} size={16} />
              <span>{isDarkMode ? 'Light Mode' : 'Dark Mode'}</span>
            </button>
            
            <button className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-popover-foreground hover:bg-muted rounded-md transition-colors duration-200">
              <Icon name="User" size={16} />
              <span>Profile Settings</span>
            </button>
            
            <button className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-popover-foreground hover:bg-muted rounded-md transition-colors duration-200">
              <Icon name="Bell" size={16} />
              <span>Notifications</span>
            </button>
            
            <button className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-popover-foreground hover:bg-muted rounded-md transition-colors duration-200">
              <Icon name="HelpCircle" size={16} />
              <span>Help & Support</span>
            </button>
            
            <div className="border-t border-border mt-2 pt-2">
              <button className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-destructive hover:bg-muted rounded-md transition-colors duration-200">
                <Icon name="LogOut" size={16} />
                <span>Sign Out</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const MobileMenu = () => (
    <div className={`lg:hidden fixed inset-0 z-300 transition-opacity duration-300 ${isMobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setIsMobileMenuOpen(false)} />
      
      <div className={`absolute right-0 top-0 h-full w-80 bg-card border-l border-border shadow-lg transform transition-transform duration-300 ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="flex items-center justify-between p-4 border-b border-border">
          <Logo />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>
        
        <div className="p-4 space-y-2">
          {navigationItems?.map((item) => (
            <button
              key={item?.path}
              onClick={() => handleNavigation(item?.path)}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-all duration-200 ${
                isActivePath(item?.path)
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-card-foreground hover:bg-muted'
              }`}
            >
              <Icon 
                name={item?.icon} 
                size={20} 
                className={isActivePath(item?.path) ? 'text-primary-foreground' : 'text-muted-foreground'}
              />
              <span className="font-medium">{item?.label}</span>
            </button>
          ))}
        </div>
        
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-border">
          <div className="flex items-center justify-between">
            <EnvironmentIndicator />
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleTheme}
            >
              <Icon name={isDarkMode ? "Sun" : "Moon"} size={18} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <header className="fixed top-0 left-0 right-0 h-16 bg-background/95 backdrop-blur-lg border-b border-border z-100">
        <div className="h-full px-6 flex items-center justify-between">
          <div className="flex items-center space-x-8">
            <Logo />
            
            <nav className="hidden lg:flex items-center space-x-1">
              {navigationItems?.map((item) => (
                <button
                  key={item?.path}
                  onClick={() => handleNavigation(item?.path)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isActivePath(item?.path)
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  <Icon 
                    name={item?.icon} 
                    size={16} 
                    className={isActivePath(item?.path) ? 'text-primary-foreground' : 'text-muted-foreground'}
                  />
                  <span>{item?.label}</span>
                </button>
              ))}
            </nav>
          </div>

          <div className="flex items-center space-x-4">
            <EnvironmentIndicator />
            
            <div className="hidden lg:block">
              <UserMenu />
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsMobileMenuOpen(true)}
              className="lg:hidden"
            >
              <Icon name="Menu" size={20} />
            </Button>
          </div>
        </div>
      </header>
      <MobileMenu />
      {(isUserMenuOpen || isMobileMenuOpen) && (
        <div 
          className="fixed inset-0 z-50" 
          onClick={() => {
            setIsUserMenuOpen(false);
            setIsMobileMenuOpen(false);
          }}
        />
      )}
    </>
  );
};

export default Header;